import React, { useState, useEffect } from 'react';
import { Row, Col, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosConfig';

const SparePartsPricingForm = ({ initialData = null, isEdit = false }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [vehicles, setVehicles] = useState([]);
    const [vehicleVariants, setVehicleVariants] = useState([]);
    const [errors, setErrors] = useState({});

    const [formData, setFormData] = useState({
        vehicle_id: initialData?.vehicle_id || '',
        variant_id: initialData?.variant_id || '',
        description_en: initialData?.description_en || '',
        description_ar: initialData?.description_ar || '',
        price: initialData?.price || '',
        sort_order: initialData?.sort_order || 0,
    });

    useEffect(() => {
        fetchVehicles();
    }, []);

    useEffect(() => {
        const vehicle = vehicles.find(v => v.vehicle_id === formData.vehicle_id);
        setVehicleVariants(vehicle?.variants || []);
    }, [formData.vehicle_id, vehicles]);

    const fetchVehicles = async () => {
        try {
            const response = await axiosInstance.get('/vehicles');
            const data = response.data.data || response.data || [];
            setVehicles(Array.isArray(data) ? data : []);
        } catch (error) {
            toast.error('Failed to load vehicles');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'vehicle_id' ? { variant_id: '' } : {}),
        }));
        setErrors(prev => {
            const next = { ...prev };
            delete next[name];
            return next;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const newErrors = {};
        if (!formData.vehicle_id) newErrors.vehicle_id = 'Vehicle is required';
        if (!formData.variant_id) newErrors.variant_id = 'Variant is required';
        if (!formData.description_en) newErrors.description_en = 'English description is required';
        if (!formData.description_ar) newErrors.description_ar = 'Arabic description is required';
        if (!formData.price) newErrors.price = 'Price is required';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            toast.error('Please fix the errors below');
            return;
        }

        setLoading(true);
        try {
            if (isEdit && initialData?.id) {
                await axiosInstance.put(`/spare-parts/${initialData.id}`, formData);
                toast.success('Spare part updated successfully!');
            } else {
                await axiosInstance.post('/spare-parts', formData);
                toast.success('Spare part created successfully!');
            }
            navigate('/spare-parts-management/spare-parts-pricing');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save spare part');
        } finally {
            setLoading(false);
        }
    };

    const selectedVehicle = vehicles.find(v => v.vehicle_id === formData.vehicle_id);

    return (
        <div className="spare-parts-pricing-form">
            <Form onSubmit={handleSubmit}>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Vehicle</Form.Label>
                            <Form.Select
                                name="vehicle_id"
                                value={formData.vehicle_id}
                                onChange={handleInputChange}
                                isInvalid={!!errors.vehicle_id}
                            >
                                <option value="">Select Vehicle</option>
                                {vehicles
                                    .filter(v => v.show_on_spare_parts_page || v.discontinued)
                                    .map(v => (
                                        <option key={v.id} value={v.vehicle_id}>
                                            {v.title_en}{v.discontinued ? ' (Legacy)' : ''}
                                        </option>
                                    ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">{errors.vehicle_id}</Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Variant</Form.Label>
                            {vehicleVariants.length > 0 ? (
                                <Form.Select
                                    name="variant_id"
                                    value={formData.variant_id}
                                    onChange={handleInputChange}
                                    isInvalid={!!errors.variant_id}
                                    disabled={!formData.vehicle_id}
                                >
                                    <option value="">Select Variant</option>
                                    {vehicleVariants.map(v => (
                                        <option key={v.id} value={v.name}>{v.name}</option>
                                    ))}
                                </Form.Select>
                            ) : (
                                <Form.Control
                                    type="text"
                                    name="variant_id"
                                    value={formData.variant_id}
                                    onChange={handleInputChange}
                                    placeholder={formData.vehicle_id ? 'Type variant name (e.g., GL, GLX)' : 'Select a vehicle first'}
                                    isInvalid={!!errors.variant_id}
                                    disabled={!formData.vehicle_id}
                                />
                            )}
                            {selectedVehicle && vehicleVariants.length === 0 && (
                                <Form.Text className="text-muted">No approved variants found — enter variant name manually.</Form.Text>
                            )}
                            <Form.Control.Feedback type="invalid">{errors.variant_id}</Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Description (English)</Form.Label>
                            <Form.Control
                                type="text"
                                name="description_en"
                                value={formData.description_en}
                                onChange={handleInputChange}
                                placeholder="e.g., Brake Pad Front"
                                isInvalid={!!errors.description_en}
                            />
                            <Form.Control.Feedback type="invalid">{errors.description_en}</Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Description (Arabic)</Form.Label>
                            <Form.Control
                                type="text"
                                name="description_ar"
                                value={formData.description_ar}
                                onChange={handleInputChange}
                                placeholder="بطانات الفرامل الأمامية"
                                dir="rtl"
                                isInvalid={!!errors.description_ar}
                            />
                            <Form.Control.Feedback type="invalid">{errors.description_ar}</Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Price (SAR)</Form.Label>
                            <Form.Control
                                type="number"
                                name="price"
                                value={formData.price}
                                onChange={handleInputChange}
                                placeholder="e.g., 322"
                                min="0"
                                step="0.01"
                                isInvalid={!!errors.price}
                            />
                            <Form.Control.Feedback type="invalid">{errors.price}</Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Sort Order</Form.Label>
                            <Form.Control
                                type="number"
                                name="sort_order"
                                value={formData.sort_order}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <div className="text-end mt-4">
                    <Button variant="secondary" className="me-2" onClick={() => navigate('/spare-parts-management/spare-parts-pricing')}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="primary" disabled={loading}>
                        {loading ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default SparePartsPricingForm;
